import { AsyncPipe, NgForOf, NgIf, TitleCasePipe } from '@angular/common';
import { Component, computed, ElementRef, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, MatPaginatorSelectConfig } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Array } from 'core-js';
import { Observable, of, Subscription, takeWhile, timer } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { AdminService } from '../../admin/admin.service';
import { tableRowAnimation } from '../../data-rules/rule/kyloe-animations';
import { DuplikitOptionsService } from '../../datatools-settings/duplikit-options/duplikit-options.service';
import { RippleDirective } from '../../ripple.directive';
import { DataToolsApps } from '../../shared/DataToolsApps';
import { DataToolsLiteMessage } from '../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../shared/services/datatools-lite.service';
import { DuplikitMergeService } from '../../shared/services/duplikit-merge.service';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { UserInterfaceService } from '../../shared/services/user-interface.service';
import { UtilsService } from '../../shared/services/utils.service';
import { DryRunWarningComponent } from '../dry-run-warning/dry-run-warning.component';
import { DuplicateService } from '../duplicate.service';
import { DuplicateTableEntry } from './duplicate-table-entry';
import { DuplicatesDialogComponent, IDuplicatesDialogData } from './duplicates-dialog/duplicates-dialog.component';
import { Entity } from './entity';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  imports: [
    DryRunWarningComponent,
    TitleCasePipe,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    NgIf,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatPaginator,
    FormsModule,
    NgForOf,
    RippleDirective,
    MatSort,
    MatSortHeader,
    MatIcon,
    MatTooltip,
    AsyncPipe
  ],
  animations: [tableRowAnimation]
})
export class ReportComponent implements OnInit, OnDestroy {
  protected readonly Array = Array;
  readonly pageSizeSelectConfig: MatPaginatorSelectConfig = { panelClass: 'kyloe-paginator-select-panel' };
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  mergingEntity: string;
  mergingEntityPlural: string;
  duplicateTotal: number;
  isLoading: boolean;
  timer: Subscription;
  dataSource: MatTableDataSource<DuplicateTableEntry>;
  columns: string[] = ['masterID', 'numDuplicates', 'duplicateIDs', 'button'];
  mergeNumberOptions: number[] = [10, 100, 250, 500, 750, 1000];
  mergeNumber = 10;
  maxDuplicatesPerMaster$: Observable<number>;
  duplikitDashboardLink = DataToolsApps.DUPLIKIT_DASHBOARD;
  dataSourceSubscription: Subscription;

  isExporting: WritableSignal<boolean> = signal(false);
  exportButtonLabel = computed(() => this.isExporting() ? 'Generating...' : 'Export to CSV');

  constructor(
      private router: Router,
      private route: ActivatedRoute,
      private messageNotificationService: MessageNotificationService,
      private utilsService: UtilsService,
      public duplikitMergeService: DuplikitMergeService,
      public userInterfaceService: UserInterfaceService,
      public duplicateService: DuplicateService,
      public adminService: AdminService,
      public datatoolsLiteService: DatatoolsLiteService,
      private duplikitOptionsService: DuplikitOptionsService,
      private dialog: MatDialog
  ) {
    this.mergingEntity = this.route.snapshot.data['entity'];
    this.mergingEntityPlural = this.mergingEntity === 'company' ? 'companies' : this.mergingEntity + 's';
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.refreshState();
    if (!this.adminService.isInLiteMode()) {
      this.duplikitMergeService.runMergeCheck();
    }
    this.duplicateService.getEntityDuplicateTotal(this.mergingEntity).subscribe(total => {
      this.duplicateTotal = total;
    }, (error) => {
      console.error(`Could not get ${ this.mergingEntity } duplicate total.`, error);
    });

    this.maxDuplicatesPerMaster$ = this.duplikitOptionsService.getOptions().pipe(
        map(options => options?.maxDuplicatesPerMaster),
        catchError((error) => {
          console.error('Could not get DupliKit options for the merge limit hint.', error);
          return of(undefined);
        })
    );
    this.dataSourceSubscription = this.duplicateService.results$.subscribe(res => {
      this.dataSource = new MatTableDataSource<DuplicateTableEntry>(res);
      this.dataSource.paginator = this.paginator;

      this.dataSource.sort = this.sort;
      this.dataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'numDuplicates':
            return item.duplicateIDs.size;
          case 'duplicateIDs':
            return Math.min(...Array.from(item.duplicateIDs));
          default:
            return item[property];
        }
      };

      this.dataSource.filterPredicate = (data: DuplicateTableEntry, filter: string) => {
        const masterIDMatch = data.masterID.toString() === filter;
        const duplicateIDsMatch = Array.from(data.duplicateIDs).some(id =>
          id.toString() === filter
        );
        return masterIDMatch || duplicateIDsMatch;
      };

      if (res != null) {
        this.isLoading = false;
      }
    });
  }

  applyFilter() {
    this.dataSource.filter = this.searchInput.nativeElement.value.trim();
  }

  clearFilter() {
    this.dataSource.filter = '';
    this.searchInput.nativeElement.value = '';
  }

  mergeBatch(): void {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_MULTIPLE_MERGE);
      return;
    }
    this.userInterfaceService.duplikit.mergeButtonsDisabled = true;
    if (this.duplikitMergeService.anyEntityIsMerging()) {
      this.showMergeInProgress();
      return;
    }
    this.duplikitMergeService.setEntityIsMerging(this.mergingEntity);
    this.duplicateService.dedupeEntityBatch(this.mergingEntity, this.mergeNumber).subscribe((success: boolean) => {
      if (success) {
        const queuedUp = this.mergeNumber < this.duplicateTotal ? this.mergeNumber : this.duplicateTotal;
        this.messageNotificationService.info('Merge Running', `Queued ${ queuedUp } duplicates to merge. We'll let you know when this is done.`);
        if (!this.timer || this.timer?.closed) {
          this.pollCompletion();
        }
      }
    }, (error) => {
      console.error(error);
      this.messageNotificationService.error('Failed to Merge', `An error occurred merging ${ this.utilsService.getProperEntityName(this.mergingEntity) } duplicates: </br>` + error.message);
    });
  }

  mergeSingleMaster(masterID: number): void {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_SINGLE_MERGE);
      return;
    }
    this.userInterfaceService.duplikit.mergeButtonsDisabled = true;
    this.duplikitMergeService.setEntityIsMerging(this.mergingEntity);
    this.duplicateService.dedupeEntity(this.mergingEntity, masterID).subscribe((success) => {
      if (!success) {
        this.messageNotificationService.error('Merge in Progress', 'A merge is already in progress. Please wait.');
      } else {
        this.messageNotificationService.info('Starting Merge', `This shouldn't take too long. We'll let you know when it's done.`);
        if (!this.timer || this.timer?.closed) {
          this.pollCompletion();
        }
        this.dataSource.data = this.dataSource.data.filter(dataSourceRow => dataSourceRow.masterID !== masterID);
      }
    }, (error) => {
      console.error(error);
      this.messageNotificationService.error(
          'Failed to Merge',
          `An error occurred merging ${ this.utilsService.getProperEntityName(this.mergingEntity) } master #${ masterID }: </br>`
          + error.message
      );
    });
  }

  downloadCSV(): void {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_CSV_EXPORT);
      return;
    }
    this.isExporting.set(true)
    this.messageNotificationService.info('Generating CSV', 'This may take a little time to create an export of up to 5000 records.');

    this.duplicateService.downloadEntityCSV(this.mergingEntity).subscribe({
      next: (data: {jobId: string}) => {
        timer(5_000, 10_000).pipe(
          switchMap(() => this.duplicateService.getExportStatus(this.mergingEntity, data.jobId)),
          takeWhile(res => res.status === 'RUNNING', true),
          timeout({first: 5 * 60 * 1000})
        ).subscribe({
          next: res => {
            if (res.status === 'COMPLETE' && res.url) {
              window.open(res.url, '_blank');
              this.messageNotificationService.success('Successfully generated CSV')
            } else if (res.status === 'FAILED') {
              this.messageNotificationService.error('Error generating CSV','There was an error generating the CSV file for export.')
            }
          },
          error: (error) => {
            console.error(error)
            this.messageNotificationService.error(
              'Error generating CSV',
              `There was an error generating the CSV file for export. </br>` + error.message
            );
            this.isExporting.set(false)
          },
          complete: () => {
            this.isExporting.set(false)
          }
        })
      },
      error: (error) => {
        console.error(error)
        this.messageNotificationService.error(
          'Error generating CSV',
          `There was an error generating the CSV file for export. </br>` + error.message
        );
        this.isExporting.set(false)
      }
    })
  }

  backToSyncPage(): void {
    this.router.navigate([this.duplikitDashboardLink]);
  }

  displayEntityIsMerging(): string {
    return `${ this.utilsService.getEntityLabel(this.duplikitMergeService.getEntityThatIsMerging()) } Merge In Progress`;
  }

  duplicatesDialog(row: DuplicateTableEntry): void {
    const dialogData: IDuplicatesDialogData = {
      mergingEntity: this.mergingEntity as Entity,
      duplicateTableEntry: row,
    };
    const dialogRef = this.dialog.open(DuplicatesDialogComponent, {
      maxWidth: '88vw',
      maxHeight: '88vh',
      height: 'auto',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe(_ => {
      this.dataSource.data = this.dataSource.data.filter(dataSourceRow => dataSourceRow.duplicateIDs.size > 0);
    });
  }

  private showMergeInProgress() {
    this.messageNotificationService.error('Merge in progress', `${ this.duplikitMergeService.getEntityThatIsMerging() } Merge in progress. Please wait until complete.`);
  }

  private pollCompletion(): void {
    this.duplikitMergeService.runMergeCheck();
    this.timer = timer(2000, 3000).subscribe(() => {
      if (!this.duplikitMergeService.getEntityThatIsMerging()) {
        this.messageNotificationService.success('Merge complete', 'Merge has completed, check History page for results.');
        this.userInterfaceService.duplikit.mergeButtonsDisabled = false;
        this.refreshState();
        this.timer.unsubscribe();
        this.duplicateService.getEntityDuplicateTotal(this.mergingEntity).subscribe(total => {
          this.duplicateTotal = total;
        }, (error) => {
          console.error(`Could not get ${ this.mergingEntity } duplicate total.`, error);
        });
      }
    });
  }

  private refreshState(): void {
    this.duplicateService.refreshState(this.mergingEntity);
  }

  ngOnDestroy(): void {
    this.duplikitMergeService.stopMergeCheck();
    if (this.timer) {
      this.timer.unsubscribe();
    }
    this.duplicateService.clearResults();
    this.dataSourceSubscription.unsubscribe();
  }
}
